// This is a placeholder for the table columns and data types for the inventory table using TanStack Table
import { ColumnDef } from '@tanstack/react-table';
import * as React from 'react';
import {
  ArrowTopRightOnSquareIcon,
  DocumentMagnifyingGlassIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { TFunction } from 'i18next';
import { ReactComponent as NoImageIcon } from '~/assets/svg/no_imagen.svg';

// Definir tipos para las variantes
export interface VariantOption {
  name: string;
  value: string;
}

export interface ProductVariant {
  id: string;
  title: string | null;
  imageURL: string | null;
  selectedOptions: VariantOption[];
  price: string;
}

export type InventoryItem = {
  _id: string;
  id?: string;
  agentId?: string; // Add agentId property
  mainImageURL: string | null;
  title: string;
  totalInventory: number;
  description: string;
  onlineStorePreviewUrl: string | null;
  priceRangeV2: {
    minVariantPrice: {
      amount: string;
      currencyCode: string;
    };
    maxVariantPrice?: {
      amount: string;
      currencyCode: string;
    };
  };
  variants?: ProductVariant[];
  updatedAt: string;
  score?: number; // Add optional score for vector search results
};

export const createColumns = (
  navigate: (path: string) => void,
  t: TFunction,
  storeType?: string,
  onDeleteProduct?: (productId: string, productTitle: string) => void,
): ColumnDef<InventoryItem>[] => [
  {
    accessorKey: 'mainImageURL',
    header: t('table.photo'),
    enableSorting: false,
    cell: (info) => {
      const value = info.getValue() as string;
      if (value) {
        return React.createElement(
          'div',
          {
            className: 'avatar',
          },
          React.createElement(
            'div',
            {
              className: 'mask mask-squircle w-12 h-12',
            },
            React.createElement('img', {
              src: value,
              alt: 'thumbnail',
            }),
          ),
        );
      } else {
        return React.createElement(
          'div',
          {
            className: 'avatar placeholder',
          },
          React.createElement(
            'div',
            {
              className:
                'bg-neutral text-neutral-content rounded-full w-12 h-12',
            },
            React.createElement(NoImageIcon, {
              className: 'w-8 h-8',
            }),
          ),
        );
      }
    },
  },
  {
    accessorKey: 'title',
    header: t('table.title'),
    enableSorting: true,
    cell: (info) => {
      const title = info.getValue() as string;
      const shortTitle = title.length > 60 ? title.slice(0, 60) + '...' : title;
      const row = info.row?.original;
      // Use a custom event to trigger dialog in parent
      return React.createElement(
        'span',
        {
          className: 'cursor-pointer text-base-content hover:text-primary',
          title: title,
          onClick: (e: React.MouseEvent) => {
            e.stopPropagation();
            const event = new CustomEvent('show-description-dialog', {
              detail: {
                title: row?.title,
                description: row?.description,
              },
              bubbles: true,
            });
            (e.target as HTMLElement).dispatchEvent(event);
          },
        },
        shortTitle,
      );
    },
  },
  {
    accessorKey: 'totalInventory',
    header: t('table.inventory'),
    enableSorting: true,
    cell: (info) => info.getValue(),
  },
  {
    accessorKey: 'priceRangeV2',
    header: t('table.price_range'),
    enableSorting: true,
    sortingFn: (rowA, rowB, columnId) => {
      const a = rowA.getValue(columnId) as {
        minVariantPrice: { amount: string; currencyCode: string };
        maxVariantPrice?: { amount: string; currencyCode: string };
      };
      const b = rowB.getValue(columnId) as {
        minVariantPrice: { amount: string; currencyCode: string };
        maxVariantPrice?: { amount: string; currencyCode: string };
      };

      const priceA = a ? Number(a.minVariantPrice.amount) : 0;
      const priceB = b ? Number(b.minVariantPrice.amount) : 0;

      return priceA < priceB ? -1 : priceA > priceB ? 1 : 0;
    },
    cell: (info) => {
      const value = info.getValue() as {
        minVariantPrice: { amount: string; currencyCode: string };
        maxVariantPrice?: { amount: string; currencyCode: string };
      };
      if (!value) return ' ';

      const formatPrice = (amount: string, currencyCode: string) => {
        const numericAmount = Number(amount);

        // Si currencyCode es null, vacío, o un símbolo como '$', formatear como número simple
        if (
          !currencyCode ||
          currencyCode === 'null' ||
          currencyCode === '$' ||
          currencyCode.length <= 2
        ) {
          return `$${numericAmount.toLocaleString('es-CO')}`;
        }

        // Si es un código de moneda válido (como USD, EUR, etc.), usar toLocaleString con currency
        try {
          return numericAmount.toLocaleString('es-CO', {
            style: 'currency',
            currency: currencyCode,
          });
        } catch (error) {
          // Si el código de moneda no es válido, mostrar como número con símbolo $
          return `$${numericAmount.toLocaleString('es-CO')}`;
        }
      };

      const min = formatPrice(
        value.minVariantPrice.amount,
        value.minVariantPrice.currencyCode,
      );
      let max = min; // Por defecto, max es igual a min

      if (value.maxVariantPrice) {
        max = formatPrice(
          value.maxVariantPrice.amount,
          value.maxVariantPrice.currencyCode,
        );
      }

      return min === max ? min : `${min} - ${max}`;
    },
  },
  {
    accessorKey: 'updatedAt',
    header: t('table.updated_at'),
    enableSorting: true,
    cell: (info) => {
      const value = info.getValue() as string;
      if (!value) return '-';

      const date = new Date(value);
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();

      return `${day}/${month}/${year}`;
    },
  },
  {
    accessorKey: 'onlineStorePreviewUrl',
    header: '',
    enableSorting: false,
    cell: (info) => {
      const value = info.getValue() as string;
      const row = info.row.original;

      // If it's tbitStore, show an edit button instead of the URL link
      if (storeType === 'tbitStore') {
        return React.createElement(
          'button',
          {
            className: 'btn btn-ghost btn-sm gap-2',
            title: t('product.edit'),
            onClick: () => {
              // Navigate to edit page
              navigate(
                `/agent/${
                  row.agentId || window.location.pathname.split('/')[2]
                }/store/edit/${row._id}`,
              );
            },
          },
          React.createElement('span', { className: 'flex items-center' }, [
            React.createElement(
              'svg',
              {
                xmlns: 'http://www.w3.org/2000/svg',
                fill: 'none',
                viewBox: '0 0 24 24',
                strokeWidth: 1.5,
                stroke: 'currentColor',
                className: 'h-4 w-4',
                key: 'edit-icon',
              },
              [
                React.createElement('path', {
                  strokeLinecap: 'round',
                  strokeLinejoin: 'round',
                  d: 'M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10',
                  key: 'edit-path',
                }),
              ],
            ),
          ]),
        );
      }

      // Default: show URL link for external stores
      return React.createElement(
        'a',
        {
          href: value,
          target: '_blank',
          rel: 'noopener noreferrer',
          className: 'btn btn-ghost btn-sm gap-2',
          title: t('product.view_in_store'),
        },
        React.createElement(ArrowTopRightOnSquareIcon, {
          className: 'h-4 w-4',
        }),
      );
    },
  },
  // Delete action column - only show for tbitStore
  ...(storeType === 'tbitStore'
    ? [
        {
          id: 'delete',
          header: '',
          enableSorting: false,
          cell: ({ row }: { row: any }) =>
            React.createElement(
              'button',
              {
                className: 'btn btn-ghost btn-sm text-error hover:bg-error/20',
                title: t('product.delete'),
                onClick: (e: React.MouseEvent) => {
                  e.stopPropagation();
                  if (onDeleteProduct) {
                    onDeleteProduct(row.original._id, row.original.title);
                  }
                },
              },
              React.createElement(TrashIcon, { className: 'h-4 w-4' }),
            ),
        },
      ]
    : []),
  {
    id: 'actions',
    header: '',
    enableSorting: false,
    cell: ({ row }) =>
      React.createElement(
        'button',
        {
          className: 'btn btn-ghost btn-sm',
          title: t('table.details'),
          onClick: () => {
            // Navega a la url actual + /id
            const current = window.location.pathname.replace(/\/$/, '');
            navigate(`${current}/${row.original._id}`);
          },
        },
        React.createElement(DocumentMagnifyingGlassIcon, {
          className: 'h-4 w-4',
        }),
      ),
  },
];
